import { useMediaQuery } from 'react-responsive';
import { CtaButtonType, CtaForm } from '@pairy/sheard/types';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';

import useViewerIntervalRequest from '../../hooks/useViewerIntervalRequest';
import DesktopPageNavigation from '../@common/PageNavigationDrawer/Desktop';
import { BulkFileResponseType } from '../../types/file';
import { EmbedItemType, MessageBoxItemType } from '../PdfViewer/index.types';
import RootLayout from '../@common/RootLayout';
import {
  currentPageAtom,
  isAnimationPlayedAtom,
  isCtaModalOpenAtom,
} from '../../store/index.atom';
import MobilePageNavigation from '../@common/PageNavigationDrawer/Mobile';
import useUpdateRenderThumbnails from '../../hooks/useUpdateRenderThumbnails';
import V2BulkViewer from '../v2/V2BulkViewer';
import { useViewerRoute } from '../../contexts/ViewerRoute';

import { getBlindPages } from './index.util';
import BulkDesktopViewer from './Desktop';
import BulkMobileViewer from './Mobile';

interface Props {
  data?: BulkFileResponseType;
  messageBoxItems?: MessageBoxItemType[];
  embedItems?: EmbedItemType[];
  ctaButtonList?: CtaButtonType[];
  ctaForm?: CtaForm;
  useWindowUnload: () => void;
  translateKoToEn: (item: string) => string;
}

const BulkRedirectViewer = ({
  data,
  messageBoxItems,
  embedItems,
  ctaButtonList,
  ctaForm,
  useWindowUnload,
  translateKoToEn,
}: Props) => {
  const userAgent =
    typeof window !== 'undefined' ? window.navigator.userAgent : '';

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const isCtaModalOpen = useAtomValue(isCtaModalOpenAtom);
  const currentPage = useAtomValue(currentPageAtom);
  const setIsAnimationPlayed = useSetAtom(isAnimationPlayedAtom);

  useWindowUnload();

  const isMobile =
    useMediaQuery({ maxWidth: 767 }) || /iPhone|Android/.test(userAgent);

  useViewerIntervalRequest({ isMobile });

  const isMobileLandscape =
    useMediaQuery({
      query: '(orientation: landscape)',
    }) && isMobile;

  useEffect(() => {
    setIsAnimationPlayed(false);
  }, []);

  const pageNavigationInfo = data?.file.thumbnails?.map(
    ({ page, thumbnail, originalHeight, originalWidth, imageSources }) => {
      return {
        page,
        thumnail: thumbnail,
        originalHeight,
        originalWidth,
        imageSources,
      };
    },
  );

  const isFormSubmitted = data?.file?.isFormSubmitted;

  const blindStatus = ctaForm?.blind;
  const blindPage = ctaForm?.page;
  const blindPages = isFormSubmitted
    ? []
    : getBlindPages({
        blindStatus,
        blindPage,
        totalPages: pageNavigationInfo?.length,
      });

  const isActiveHelpers = isCtaModalOpen && currentPage === Number(blindPage);

  const { updateNextThumbnails, updatePrevThumbnails } =
    useUpdateRenderThumbnails({
      defaultThumbnails: data?.file?.thumbnails,
    });

  // const { version } = useViewerVersion();
  const version = 'v2';
  const { isShareRoute } = useViewerRoute();

  /* 버전 해석 전 단일 프레임: V1 플래시를 막기 위해 렌더를 보류한다 */
  if (isShareRoute && version === null) {
    return null;
  }

  if (isShareRoute && version === 'v2' && data) {
    return (
      <V2BulkViewer
        data={data}
        messageBoxItems={messageBoxItems}
        embedItems={embedItems}
        ctaButtonList={ctaButtonList}
        ctaForm={ctaForm}
        translateKoToEn={translateKoToEn}
      />
    );
  }

  if (isMobile) {
    return (
      <RootLayout mode="mobile">
        {data && (
          <>
            <BulkMobileViewer
              file={data.file}
              user={data.user}
              translateKoToEn={translateKoToEn}
              ctaForm={ctaForm}
              messageBoxItems={messageBoxItems}
              ctaButtonList={ctaButtonList}
              updateNextThumbnails={updateNextThumbnails}
              updatePrevThumbnails={updatePrevThumbnails}
              setIsMobileDrawerOpen={setIsMobileDrawerOpen}
              isMobileLandscape={isMobileLandscape}
            />
            <MobilePageNavigation
              translateKoToEn={translateKoToEn}
              pages={pageNavigationInfo}
              blindPages={blindPages}
              isMobileDrawerOpen={isMobileDrawerOpen}
              setIsMobileDrawerOpen={setIsMobileDrawerOpen}
              isMobileLandscape={isMobileLandscape}
            />
          </>
        )}
      </RootLayout>
    );
  }

  return (
    <RootLayout mode="desktop" isActiveHelpers={isActiveHelpers}>
      {data && (
        <>
          <DesktopPageNavigation
            fileName={data?.file.name}
            pages={pageNavigationInfo}
            translateKoToEn={translateKoToEn}
            blindPages={blindPages}
          />
          <BulkDesktopViewer
            file={data.file}
            user={data.user}
            ctaForm={ctaForm}
            messageBoxItems={messageBoxItems}
            ctaButtonList={ctaButtonList}
            blindPages={blindPages}
            translateKoToEn={translateKoToEn}
            updateNextThumbnails={updateNextThumbnails}
            updatePrevThumbnails={updatePrevThumbnails}
          />
        </>
      )}
    </RootLayout>
  );
};

export default BulkRedirectViewer;
